import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiPause, FiX } from 'react-icons/fi';
import memberService from '../services/member.service';
import S3Image from '../components/S3Image';

const FreezeMember = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [member, setMember] = useState(null);

  const [formData, setFormData] = useState({
    freeze_start_date: '',
    freeze_end_date: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchMember();
  }, [id]);

  const fetchMember = async () => {
    try {
      const response = await memberService.getMemberById(id);
      setMember(response.data);
    } catch (error) {
      toast.error('Failed to fetch member details');
      navigate('/members');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.freeze_start_date) {
      newErrors.freeze_start_date = 'Freeze start date is required';
    }

    if (!formData.freeze_end_date) {
      newErrors.freeze_end_date = 'Freeze end date is required';
    }

    if (formData.freeze_start_date && formData.freeze_end_date) {
      const startDate = new Date(formData.freeze_start_date);
      const endDate = new Date(formData.freeze_end_date);

      if (endDate <= startDate) {
        newErrors.freeze_end_date = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setLoading(true);
      await memberService.freezeMembership(id, formData);
      toast.success('Membership frozen successfully');
      navigate(`/members/${id}`);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to freeze membership';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!member) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/members/${id}`)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft className="mr-2" />
          Back to Member Details
        </button>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-lg shadow">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <FiPause className="text-yellow-600 mr-3" size={24} />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Freeze Membership</h2>
              <p className="text-sm text-gray-600 mt-1">
                Freeze membership for {member.first_name} {member.last_name}
              </p>
            </div>
          </div>
        </div>

        {/* Member Info */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <S3Image
              src={member.photo}
              alt={`${member.first_name} ${member.last_name}`}
              className="w-16 h-16 rounded-full object-cover"
              fallback={
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-xl">
                  {member.first_name?.[0]}{member.last_name?.[0]}
                </div>
              }
            />
            <div>
              <h3 className="font-semibold text-gray-900">
                {member.first_name} {member.last_name}
              </h3>
              <p className="text-sm text-gray-600">Member ID: {member.member_id}</p>
              {member.plan_name && (
                <p className="text-sm text-gray-600">Current Plan: {member.plan_name}</p>
              )}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Info Message */}
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Freezing a membership will temporarily suspend access to gym facilities.
              The membership period will be extended by the freeze duration.
            </p>
          </div>

          {/* Freeze Start Date */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Freeze Start Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="freeze_start_date"
              value={formData.freeze_start_date}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.freeze_start_date ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.freeze_start_date && (
              <p className="mt-1 text-sm text-red-500">{errors.freeze_start_date}</p>
            )}
          </div>

          {/* Freeze End Date */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Freeze End Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="freeze_end_date"
              value={formData.freeze_end_date}
              onChange={handleChange}
              min={formData.freeze_start_date || new Date().toISOString().split('T')[0]}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.freeze_end_date ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.freeze_end_date && (
              <p className="mt-1 text-sm text-red-500">{errors.freeze_end_date}</p>
            )}
          </div>

          {/* Duration Display */}
          {formData.freeze_start_date && formData.freeze_end_date && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Freeze Duration:</strong>{' '}
                {Math.ceil(
                  (new Date(formData.freeze_end_date) - new Date(formData.freeze_start_date)) /
                  (1000 * 60 * 60 * 24)
                )}{' '}
                days
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate(`/members/${id}`)}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FiX className="mr-2" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiPause className="mr-2" />
              {loading ? 'Freezing...' : 'Freeze Membership'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FreezeMember;
